import classnames from 'classnames';
import {Map} from 'immutable';
import React, {useEffect, useState} from 'react';
import {Link} from 'react-router';

import {callTargetedAction} from 'client/framework';
import {drawerActions} from 'client/generic/Drawer/DrawerActions';
import sessionStore from 'client/Session/SessionStore';
import Drawer, {colorSchemes} from 'generic/Drawer/Drawer.react';

import AssignmentsFoldersDropdown from '../AssignmentsFoldersDropdown.react';

import {getTeacherHasClassSubjects} from '../GlobalNav.queries';
import {getNavigationLinksForPath} from '../navLinks';
import {HelpCenterLink, HelpMenu} from '../NavWidgets/NavWidgets.react';

import './global-nav-drawer.scss';

interface GlobalNavDrawerProps {
  pathname: string;
}

const GlobalNavDrawer = ({pathname}: GlobalNavDrawerProps) => {
  const [isTeacherWithClassSubjects, setIsTeacherWithClassSubjects] = useState(false);

  useEffect(() => {
    if (sessionStore.isTeacher()) {
      (async () => {
        const teacherHasClassSubjects = await getTeacherHasClassSubjects();
        setIsTeacherWithClassSubjects(teacherHasClassSubjects);
      })();
    }
  }, [setIsTeacherWithClassSubjects]);

  const getIsMatchingRoute = (link) => {
    const linkRoute = link.get('route');
    const routesArr = link.get('routesArr');
    let isMatchingRoute = false;

    if (routesArr) {
      isMatchingRoute = routesArr.some((route) => pathname.startsWith(route));
    } else if (
      /**
       * This ensures that the Subjects header link will contain the active class
       * regardless of which group/subject combination is rendered. It will be replaced soon.
       */
      (link.has('matchingRoutes') && link.get('matchingRoutes').contains(pathname)) ||
      linkRoute === pathname ||
      pathname.startsWith(linkRoute) ||
      (link.has('matchingRoutes') &&
        link
          .get('matchingRoutes')
          .filter((path) => pathname.indexOf(path) === 0)
          .count() > 0)
    ) {
      isMatchingRoute = true;
    }
    return isMatchingRoute;
  };

  const handleOpenGlobalDrawer = () => {
    callTargetedAction({
      name: drawerActions.DRAWER_SET_DRAWER_VISIBILITY,
      payload: true,
      targetStore: 'GlobalNavDrawerStore'
    });
  };

  const toggleIconOnKeyPress = (e) => {
    if (e.which === 13) {
      // Return key
      handleOpenGlobalDrawer();
    }
  };

  const links = getNavigationLinksForPath(pathname, {
    isTeacherWithClassSubjects
  });

  const onMenuItemClick = () => {
    callTargetedAction({
      name: drawerActions.DRAWER_SET_DRAWER_VISIBILITY,
      payload: false,
      targetStore: 'GlobalNavDrawerStore'
    });
  };

  return (
    <div className='global-nav-drawer' onClick={handleOpenGlobalDrawer} role='button' tabIndex={0}>
      <span
        className='global-nav-drawer__toggle'
        role='button'
        onKeyDown={toggleIconOnKeyPress}
        tabIndex={0}
      >
        Menu
      </span>
      <Drawer
        colorScheme={colorSchemes.BLUE}
        direction='left'
        hasCloseButton={false}
        drawerTitle='Menu'
        storeName='GlobalNavDrawerStore'
      >
        <div className='global-nav-drawer__links-wrapper'>
          {/* @ts-ignore link is defined */}
          {links.map((link: Map<string, any>, key) => {
            const isMatchingRoute = getIsMatchingRoute(link);
            const linkClasses = classnames('global-nav-drawer__link', {
              'global-nav-drawer__link--active': isMatchingRoute
            });

            if (link.has('dropdownLinks')) {
              return <GlobalNavDrawerDropdown link={link} />;
            }

            if (link.get('key') === 'assignments') {
              return (
                // @ts-ignore
                <AssignmentsFoldersDropdown
                  key={key}
                  linkClasses={linkClasses}
                  link={link}
                  onMenuItemClick={onMenuItemClick}
                  isForDrawer
                  pathname={pathname}
                />
              );
            }

            return link.get('hardLink', false) ? (
              <a key={key} href={link.get('route')} className={linkClasses}>
                {link.get('name')}
              </a>
            ) : (
              <Link
                key={key}
                to={link.get('route')}
                className={linkClasses}
                onClick={(e) => {
                  e.stopPropagation();
                  callTargetedAction({
                    name: drawerActions.DRAWER_SET_DRAWER_VISIBILITY,
                    payload: false,
                    targetStore: 'GlobalNavDrawerStore'
                  });
                }}
              >
                {link.get('name')}
              </Link>
            );
          })}
          {sessionStore.isStudent() ? (
            <HelpCenterLink className='global-nav-drawer__link' />
          ) : (
            <HelpMenu isForDrawer />
          )}
        </div>
      </Drawer>
    </div>
  );
};

interface GlobalNavDrawerDropdownProps {
  link: Map<string, any>;
}

const GlobalNavDrawerDropdown = ({link}: GlobalNavDrawerDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  };

  const dropdownClasses = classnames('global-nav-drawer__link-dropdown', {
    'global-nav-drawer__link-dropdown--open': isOpen
  });

  return (
    <div
      className='global-nav-drawer__link global-nav-drawer__link-dropdown-wrapper'
      onClick={toggleDropdown}
      role='button'
      tabIndex={0}
    >
      {link.get('name')} <span className='fa fa-caret-down' />
      <div className={dropdownClasses}>
        {link.get('dropdownLinks').map((link, key) => {
          return link.get('hardLink', false) ? (
            <a href={link.get('route')} key={key}>
              {link.get('name')}
            </a>
          ) : (
            <Link
              key={key}
              to={link.get('route')}
              onClick={(e) => {
                e.stopPropagation();
                callTargetedAction({
                  name: drawerActions.DRAWER_SET_DRAWER_VISIBILITY,
                  payload: false,
                  targetStore: 'GlobalNavDrawerStore'
                });
              }}
            >
              {link.get('name')}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default GlobalNavDrawer;
